import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type TranslationModule = Record<string, string>;

type TagsAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  existingTags?: string[]; // Tags already selected (to avoid duplicates)
  translations?: TranslationModule;
};

/**
 * Autocomplete for tags - uses Frappe's official Tag API
 * Fetches tags once, filters client-side for performance
 */
export function TagsAutocomplete({
  value,
  onChange,
  placeholder = "Enter tag names...",
  disabled = false,
  existingTags = [],
  translations,
}: TagsAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const t = translations || {};

  // Fetch ALL tags ONCE (not per keystroke) - using Frappe's official Tag API
  const { data: allTags = [], isLoading } = useQuery<string[]>({
    queryKey: ["item-tags"],
    queryFn: async () => {
      const client = getERPClient();
      try {
        // Use Frappe's official Tag API
        // txt parameter is REQUIRED - use empty string to get all tags
        const tags = await client.call<string[]>("frappe.desk.doctype.tag.tag.get_tags", {
          txt: "", // REQUIRED: search text (empty string to get all tags)
          doctype: "Item",
        });
        
        // Normalize tags: lowercase, trimmed, no duplicates
        const normalized = Array.from(
          new Set(
            tags
              .map((tag) => tag.trim().toLowerCase())
              .filter((tag) => tag.length > 0)
          )
        );
        
        return normalized.sort();
      } catch (error) {
        console.error("Failed to fetch tags:", error);
        return [];
      }
    },
    staleTime: Infinity, // Cache forever - tags don't change frequently
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Filter tags client-side based on search term
  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) {
      // Show all tags (excluding already selected ones)
      return allTags.filter((tag) => !existingTags.includes(tag));
    }

    const searchLower = searchTerm.trim().toLowerCase();
    return allTags
      .filter((tag) => {
        // Exclude already selected tags
        if (existingTags.includes(tag)) return false;
        // Filter by search term
        return tag.includes(searchLower);
      })
      .slice(0, 20); // Limit to 20 suggestions for performance
  }, [allTags, searchTerm, existingTags]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < filteredTags.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex !== -1 && filteredTags[selectedIndex]) {
        e.preventDefault();
        handleSelect(filteredTags[selectedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredTags, selectedIndex]);

  const handleSelect = (selectedTag: string) => {
    // Normalize tag: lowercase, trimmed
    const normalized = selectedTag.trim().toLowerCase();
    if (normalized && !existingTags.includes(normalized)) {
      onChange(normalized);
      setSearchTerm("");
      setSelectedIndex(-1);
      setOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setSelectedIndex(-1);
    setOpen(true);
  };

  const handleCommaOrEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const normalized = searchTerm.trim().toLowerCase();
      if (normalized && !existingTags.includes(normalized)) {
        onChange(normalized);
        setSearchTerm("");
        setOpen(false);
      }
    }
  };

  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              type="text"
              value={searchTerm || value}
              onChange={handleInputChange}
              onKeyDown={handleCommaOrEnter}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" 
          align="start" 
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList ref={listRef} className="max-h-[200px] overflow-auto">
              {isLoading && (
                <CommandEmpty>{t.loadingTags || "Loading tags..."}</CommandEmpty>
              )}
              {!isLoading && filteredTags.length === 0 && searchTerm && (
                <CommandEmpty>
                  {t.noTagsFound || "No tags found. Press Enter or comma to add."}
                </CommandEmpty>
              )}
              {!isLoading && filteredTags.length === 0 && !searchTerm && (
                <CommandEmpty>
                  {t.startTypingToSearchTags || "Start typing to search tags..."}
                </CommandEmpty>
              )}
              {!isLoading && filteredTags.length > 0 && (
                <CommandGroup>
                  {filteredTags.map((tag, index) => {
                    const isSelected = selectedIndex === index;
                    return (
                      <CommandItem
                        key={tag}
                        value={tag}
                        onSelect={() => handleSelect(tag)}
                        className={cn(isSelected && "bg-accent")}
                      >
                        <Check className={cn("mr-2 h-4 w-4", "opacity-0")} />
                        <span>{tag}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

